import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { isBetaMode, isBetaTester } from './lib/beta-gate';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { ConditionalGoogleMapsProvider } from './components/maps/ConditionalGoogleMapsProvider';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { DesignSystemV2Provider, useDesignSystemV2, type DSV2Theme } from './context/DesignSystemV2Context';
import { TutorialProvider } from './context/TutorialContext';
import { DailyCheckInProvider } from './context/DailyCheckInContext';
import { AppSettingsProvider } from './context/AppSettingsContext';
import { CosmeticsProvider } from './context/CosmeticsContext';
import { ExperienceProvider, useExperience } from './context/ExperienceContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ImpersonateProvider } from './context/ImpersonateContext';
import { TravellerTypeProvider } from './hooks/useTravellerType';
import { OnboardingProvider } from './context/OnboardingContext';
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import { SpotlightTour } from './components/mascot/SpotlightTour';
import { FirstTripWizard } from './components/onboarding/FirstTripWizard';
import { DarumaFab } from './components/mascot/DarumaFab';
import { TutorialOverlay } from './components/tutorial/TutorialOverlay';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { CookieConsentBanner } from './components/consent/CookieConsentBanner';
import { HelpDrawerProvider } from './components/tutorial/HelpDrawer';
import { SearchProvider } from './components/search/GlobalSearch';
import { OfflineBanner } from './components/ui/OfflineBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './context/AuthContext';
import { ProtectedRoute, GuestOnlyRoute, AdminRoute, GamificationOnlyRoute } from './components/auth/ProtectedRoute';
import { initErrorLogging } from './lib/error-logger';

initErrorLogging();

const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const SignInPage = lazy(() => import('./pages/SignInPage').then(m => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import('./pages/SignUpPage').then(m => ({ default: m.SignUpPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const LocationForm = lazy(() => import('./components/locations/LocationForm').then(m => ({ default: m.LocationForm })));
const TripPlannerPage = lazy(() => import('./pages/TripPlannerPageEnhanced').then(m => ({ default: m.TripPlannerPageEnhanced })));
const DreamPage = lazy(() => import('./pages/DreamPage').then(m => ({ default: m.DreamPage })));
const TravelPage = lazy(() => import('./pages/TravelPage').then(m => ({ default: m.TravelPage })));
const SharePage = lazy(() => import('./pages/SharePage').then(m => ({ default: m.SharePage })));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage').then(m => ({ default: m.AchievementsPage })));
const CreditsPage = lazy(() => import('./pages/CreditsPage').then(m => ({ default: m.CreditsPage })));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const SharedTripPage = lazy(() => import('./pages/SharedTripPage').then(m => ({ default: m.SharedTripPage })));
const AgentDashboardPage = lazy(() => import('./pages/AgentDashboardPage').then(m => ({ default: m.AgentDashboardPage })));
const HelpPage = lazy(() => import('./pages/HelpPage').then(m => ({ default: m.HelpPage })));
const TravelEssentialsPage = lazy(() => import('./pages/TravelEssentialsPage').then(m => ({ default: m.TravelEssentialsPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then(m => ({ default: m.FaqPage })));
const TaxFreePage = lazy(() => import('./pages/TaxFreePage').then(m => ({ default: m.TaxFreePage })));
const TaxFreeTrackerPage = lazy(() => import('./pages/TaxFreeTrackerPage').then(m => ({ default: m.TaxFreeTrackerPage })));
const TaxFreeChecklistPage = lazy(() => import('./pages/TaxFreeChecklistPage').then(m => ({ default: m.TaxFreeChecklistPage })));
const TaxFreeRefundSummaryPage = lazy(() => import('./pages/TaxFreeRefundSummaryPage').then(m => ({ default: m.TaxFreeRefundSummaryPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const CookiesPage = lazy(() => import('./pages/CookiesPage').then(m => ({ default: m.CookiesPage })));
const SpotDetailPage = lazy(() => import('./pages/SpotDetailPage').then(m => ({ default: m.SpotDetailPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage').then(m => ({ default: m.ComingSoonPage })));
const BetaWaitlistPage = lazy(() => import('./pages/BetaWaitlistPage').then(m => ({ default: m.BetaWaitlistPage })));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })));
const BetaSignInPage = lazy(() => import('./pages/BetaSignInPage').then(m => ({ default: m.BetaSignInPage })));
const BetaSignUpPage = lazy(() => import('./pages/BetaSignUpPage').then(m => ({ default: m.BetaSignUpPage })));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center animate-fade-in">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 dark:bg-secondary-900/30 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-gray-500 dark:text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

function BetaGate({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useAuth();
  const location = useLocation();
  const [betaAllowed, setBetaAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isBetaMode()) { setBetaAllowed(true); return; }
    if (loading) return;
    if (!user) { setBetaAllowed(false); return; }
    if (isAdmin) { setBetaAllowed(true); return; }
    isBetaTester(user.email || '').then(setBetaAllowed);
  }, [user, isAdmin, loading]);

  if (!isBetaMode()) return <>{children}</>;
  // /unsubscribe must always work, even during beta mode for anonymous waitlist users
  if (location.pathname === '/unsubscribe') return <>{children}</>;
  if (loading || betaAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    const allowedPaths = ['/signin', '/signup', '/reset-password', '/beta-signin', '/beta-signup', '/tax-free', '/privacy', '/terms', '/cookies', '/blog', '/about', '/contact', '/help', '/faq', '/essentials'];
    if (!allowedPaths.includes(location.pathname) && !location.pathname.startsWith('/blog/')) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <ComingSoonPage />
        </Suspense>
      );
    }
  } else if (!betaAllowed) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <BetaWaitlistPage />
      </Suspense>
    );
  }

  return <>{children}</>;
}

/** Defaults non-authenticated visitors to light mode so public / beta
 * pages open in New Standard light by default. The dark/light toggle
 * in the navbar still works after the page is loaded — we only set
 * light on auth-state transitions (mount as guest, or sign-out),
 * NOT on every theme change. By keeping `theme` out of the deps array
 * the effect can't snap a manually-toggled dark theme back to light.
 * Signed-in users are untouched and keep their stored preference. */
function ForcePublicLightMode() {
  const { user, loading } = useAuth();
  const { setTheme } = useTheme();

  useEffect(() => {
    // Wait for auth check to finish; only force light for genuinely
    // non-authenticated visitors, never during the loading splash.
    if (!loading && !user) {
      setTheme('light');
    }
  }, [user, loading, setTheme]);

  return null;
}

/** Bridges ExperienceContext mode → V2 theme. Mounted inside
 * ExperienceProvider (which itself sits inside DesignSystemV2Provider)
 * so both hooks resolve. Manga / Ukiyo-e modes from the cosmetics shop
 * activate the matching V2 theme; everything else falls back to
 * 'new-standard'. Skipped while admin is in V1 legacy mode. */
function V2ExperienceBridge() {
  const { activeMode } = useExperience();
  const { setV2Theme, isV1Legacy } = useDesignSystemV2();

  useEffect(() => {
    if (isV1Legacy) return;
    const v2Theme: DSV2Theme = activeMode === 'exp-manga' ? 'new-manga'
      : activeMode === 'exp-manga-v3' ? 'new-standard'
      : activeMode === 'exp-ukiyo-e' ? 'new-ukiyo'
      : 'new-standard';
    setV2Theme(v2Theme);
  }, [activeMode, setV2Theme, isV1Legacy]);

  return null;
}

/** Manga V3 is a light-only theme. While it's equipped, force light mode
 * (remembering the user's dark preference) and restore it on unequip. */
function V3LightForcer() {
  const { isMangaV3 } = useExperience();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (isMangaV3) {
      if (theme === 'dark') {
        localStorage.setItem('theme-pre-v3', 'dark');
        setTheme('light');
      }
    } else {
      const saved = localStorage.getItem('theme-pre-v3');
      if (saved === 'dark') {
        setTheme('dark');
      }
      localStorage.removeItem('theme-pre-v3');
    }
    // Intentionally only react to isMangaV3 toggling — not every theme change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMangaV3]);

  return null;
}

/** Paints the halftone-dot pattern on <body> (behind everything) when V3 is
 * equipped, then forces the standard page-root container
 * (`min-h-screen bg-gray-50`) to be transparent so the pattern shows through.
 * Avoids an overlay div so the dots never sit on top of cards / text. The
 * `body.v3-bg` class is toggled from a useEffect so the bg lives on the
 * actual body element. */
function V3PageBackground() {
  const { isMangaV3 } = useExperience();
  useEffect(() => {
    document.body.classList.toggle('v3-bg', isMangaV3);
    return () => { document.body.classList.remove('v3-bg'); };
  }, [isMangaV3]);
  if (!isMangaV3) return null;
  return (
    <style>{`
      body.v3-bg {
        background-color: #fff !important;
        background-image: radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px) !important;
        background-size: 6px 6px !important;
        background-attachment: fixed !important;
      }
      body.v3-bg .min-h-screen.bg-gray-50 {
        background-color: transparent !important;
      }
    `}</style>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LanguageHtmlSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = 'ltr'; // all 6 languages are LTR
  }, [i18n.language]);
  return null;
}

function AuthDependentAppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Router>
      <ScrollToTop />
      <LanguageHtmlSync />
      <div className="flex flex-col min-h-screen">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:shadow-lg">
          Skip to content
        </a>
        {/* Default Toaster is hidden — DarumaFab intercepts the queue and
            renders each toast as a speech bubble next to the mascot. */}
        <Toaster position="top-right" containerStyle={{ display: 'none' }} />
        <OnboardingWizard />
        <SpotlightTour />
        <FirstTripWizard />
        <TutorialOverlay />
        <DarumaFab />
        <HelpDrawerProvider>
        <SearchProvider>
        <OfflineBanner />
        <Suspense fallback={<LoadingFallback />}>
        <BetaGate>
        <Navbar />
        <main id="main-content" className="flex-grow overflow-x-clip">
          <ErrorBoundary>
              <Routes>
                <Route path="/" element={user ? <DashboardPage /> : <HomePage />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/signin" element={<GuestOnlyRoute><SignInPage /></GuestOnlyRoute>} />
                <Route path="/signup" element={<GuestOnlyRoute><SignUpPage /></GuestOnlyRoute>} />
                <Route path="/reset-password" element={<GuestOnlyRoute><ResetPasswordPage /></GuestOnlyRoute>} />

                <Route path="/dream" element={<DreamPage />} />
                <Route path="/travel" element={<ProtectedRoute><TravelPage /></ProtectedRoute>} />
                <Route path="/share" element={<ProtectedRoute><SharePage /></ProtectedRoute>} />
                <Route path="/achievements" element={<GamificationOnlyRoute><AchievementsPage /></GamificationOnlyRoute>} />
                <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
                <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

                <Route path="/discover" element={<Navigate to="/dream" replace />} />
                <Route path="/explore" element={<Navigate to="/dream" replace />} />
                <Route path="/my-trips" element={<Navigate to="/travel" replace />} />
                <Route path="/passport" element={<Navigate to="/dream?tab=passport" replace />} />
                <Route path="/rewards" element={<Navigate to="/achievements" replace />} />
                <Route path="/community" element={<Navigate to="/share" replace />} />
                <Route path="/locations" element={<Navigate to="/dream?tab=library" replace />} />
                <Route path="/shop" element={<Navigate to="/credits?tab=shop" replace />} />
                <Route path="/merch" element={<Navigate to="/credits?tab=merch" replace />} />
                <Route path="/trips" element={<Navigate to="/travel" replace />} />
                <Route path="/plan" element={<Navigate to="/subscription" replace />} />
                <Route path="/plans" element={<Navigate to="/subscription" replace />} />
                <Route path="/pricing" element={<Navigate to="/subscription" replace />} />
                <Route path="/settings" element={<Navigate to="/profile" replace />} />
                <Route path="/badges" element={<Navigate to="/achievements" replace />} />

                <Route path="/locations/new" element={<ProtectedRoute><LocationForm /></ProtectedRoute>} />
                <Route path="/locations/:id/edit" element={<ProtectedRoute><LocationForm /></ProtectedRoute>} />
                <Route path="/trip-planner" element={<ProtectedRoute><TripPlannerPage /></ProtectedRoute>} />
                <Route path="/trip-planner/:id" element={<ProtectedRoute><TripPlannerPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/spot/:id" element={<SpotDetailPage />} />
                <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
                <Route path="/agent" element={<ProtectedRoute><AgentDashboardPage /></ProtectedRoute>} />
                <Route path="/admin/featured-spots" element={<Navigate to="/admin" replace />} />

                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/shared/:id" element={<SharedTripPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/essentials" element={<TravelEssentialsPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/tax-free" element={<TaxFreePage />} />
                <Route path="/tax-free/tracker" element={<ProtectedRoute><TaxFreeTrackerPage /></ProtectedRoute>} />
                <Route path="/tax-free/checklist" element={<ProtectedRoute><TaxFreeChecklistPage /></ProtectedRoute>} />
                <Route path="/tax-free/summary" element={<ProtectedRoute><TaxFreeRefundSummaryPage /></ProtectedRoute>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/beta-signin" element={<GuestOnlyRoute><BetaSignInPage /></GuestOnlyRoute>} />
                <Route path="/beta-signup" element={<GuestOnlyRoute><BetaSignUpPage /></GuestOnlyRoute>} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
          </ErrorBoundary>
        </main>
        <Footer />
        </BetaGate>
        </Suspense>
        <CookieConsentBanner />
        </SearchProvider>
        </HelpDrawerProvider>
      </div>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
    <ConditionalGoogleMapsProvider>
      <ThemeProvider>
        <AuthProvider>
          <DesignSystemV2Provider>
          <ForcePublicLightMode />
          <ImpersonateProvider>
          <AppSettingsProvider>
            <SubscriptionProvider>
            <CosmeticsProvider>
              <ExperienceProvider>
                <V2ExperienceBridge />
                <V3LightForcer />
                <V3PageBackground />
                <TutorialProvider>
                  <TravellerTypeProvider>
                    <OnboardingProvider>
                      <DailyCheckInProvider>
                        <AuthDependentAppContent />
                      </DailyCheckInProvider>
                    </OnboardingProvider>
                  </TravellerTypeProvider>
                </TutorialProvider>
              </ExperienceProvider>
            </CosmeticsProvider>
            </SubscriptionProvider>
          </AppSettingsProvider>
          </ImpersonateProvider>
          </DesignSystemV2Provider>
        </AuthProvider>
      </ThemeProvider>
    </ConditionalGoogleMapsProvider>
    </HelmetProvider>
  );
}

export default App;
